import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  Fab,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  Stack,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router';
import { ONLINE_MEDIA_CLIPPING } from 'src/route';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import useDownload from 'src/services/useDownload';
import { fillParamAdvanceSearchEmpty } from 'src/utils/fillParamAdvanceSearchEmpth';
import { useMemo, useState, useEffect, useRef } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import useQueryUrl from 'src/hooks/useQuery';
import useOnlineMedia from 'src/services/useOnlineMedia';
import { useAlert } from 'src/hooks/useAlert';
import { generateWhatsappReportDoc } from 'src/utils/generateWhatsappReportDoc';
import { Packer } from 'docx';
import saveAs from 'file-saver';
import { generateArticleBySentimentDoc } from 'src/utils/generateArticleBySentimentDoc';
import { useQuery } from '@apollo/client';
import { ONLINE_MEDIA_GET_CLIPPING_BY_ID_V5 } from 'src/services/graphql/onlineMedia/query';
import TableSelectGroupPortal from 'src/components/TableSelectGroupPortal';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { IGroupPortal } from 'src/models/groupPortal';
import useGroupPortal from 'src/services/useGroupPortal';
import {
  toggleSelectedGroupPortalIdOnm,
  toggleSelectedGroupPortalIdOnmExclude,
  updateSelectedGroupPortalIdOnm
} from 'src/features/groupPortal';
import {
  reducerUpdateAdvanceSearchOnmExcludeMediaList,
  reducerUpdateAdvanceSearchOnmMediaList
} from 'src/features/advanceSearchOnlineMedia';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';

const HeaderWrapper = styled(Box)(
  ({ theme }) => `
    color: ${theme.header.textColor};
    padding: ${theme.spacing(1, 4, 0, 4)};
    right: 0;
    z-index: 5;
    box-shadow: ${theme.header.boxShadow};
    justify-content: space-between;
    width: 100%;
    background: ${theme.header.background};
    @media (min-width: ${theme.breakpoints.values.lg}px) {
        left: ${theme.sidebar.width};
        width: auto;
    }
`
);

const ContainerUpload = styled(Box)(
  ({ theme }) => `
  padding: 0 8px;
  pointer-events: none;
  border-radius: inherit;
  border-style: solid;
  border-width: 1px;
  overflow: hidden;
  min-width: 0%;
  border-color: rgba(0, 0, 0, 0.23);
  border-radius: ${theme.spacing(1)};
  display: flex;
  justify-content: space-between;
  min-height: ${theme.spacing(5)};
  align-items: center;
  cursor: pointer;
  overflow: hidden;
  `
);

function HeaderOnlineMediaClippingById() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { pathname } = useLocation();
  const id = pathname.split('/')[3];
  const { handleClickAlert } = useAlert();
  const query = useQueryUrl();
  const tabClipping = query.get('tab');
  const dispatch = useAppDispatch();

  const { data, loading, error } = useQuery(
    ONLINE_MEDIA_GET_CLIPPING_BY_ID_V5,
    { variables: { clippingId: id } }
  );

  const clippingName = data?.onlineMedia_getClippingByIdV5?.name ?? '';

  const {
    downloadCsvMediaOnline,
    downloadSummaryOnlineMedia,
    resDownloadSummaryOnlineMedia
  } = useDownload();
  const { downloadClusteringCsv } = useOnlineMedia();
  const projectId = useAppSelector((state) => state.users.project.id);
  const advanceSearchOnlineMedia = useAppSelector(
    (state) => state.storeAdvanceSearchOnlineMedia
  );
  const { dateRangeFormatted } = useAppSelector(
    (state) => state.analyticArguments
  );
  const articles = useAppSelector(
    (state) => state.storeOnlineMediaStream.articleList
  );

  const clusteringConfig = useAppSelector(
    (state) => state.storeOnlineMediaStream.clusteringConfig
  );

  const fillAdvanceSearchOnlineMedia = useMemo(() => {
    return fillParamAdvanceSearchEmpty(advanceSearchOnlineMedia);
  }, [advanceSearchOnlineMedia]);

  const { languageSelected } = useAppSelector(
    (state) => state.storeOnlineMediaStream
  );

  const labels = useAppSelector((state) => state.storeLabel.labelArticle.data);
  const timezone = useAppSelector((state) => state.users.project.timezone);

  const payloads = {
    projectId,
    timezone,
    start: dateRangeFormatted.start,
    end: dateRangeFormatted.end,
    granularity: {
      unit: 'day',
      value: 1
    },
    ...fillAdvanceSearchOnlineMedia,
    clippingId: id,
    labelList: fillAdvanceSearchOnlineMedia.labelList.map(
      (label) => label.id || label
    ),
    languageList: languageSelected === 'all-language' ? [] : [languageSelected]
  };

  const [anchorElDownload, setAnchorElDownload] =
    useState<HTMLButtonElement | null>(null);

  const [openPopupAddLogo, setOpenPopupAddLogo] = useState<boolean>(false);
  const [logoPath, setLogoPath] = useState<string>('');
  const [logoName, setLogoName] = useState<string>('Choose an image');

  const handleClickDownload = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElDownload(event.currentTarget);
  };

  const handleCloseDownload = () => {
    setAnchorElDownload(null);
  };

  const handleDownload = () => {
    handleCloseDownload();
    tabClipping !== 'cluster'
      ? downloadCsvMediaOnline({
          variables: payloads
        })
      : downloadClusteringCsv({
          variables: {
            ...payloads,
            ...clusteringConfig
          },
          onCompleted: () =>
            handleClickAlert({
              horizontal: 'center',
              vertical: 'top',
              severity: 'success',
              message:
                'It may take some time for the report to be processed.\n The report can be downloaded from the Activity List on the top right corner.'
            }),
          onError(error) {
            handleClickAlert({
              horizontal: 'center',
              vertical: 'top',
              severity: 'error',
              message: 'Failed to download report, please try again.'
            });
            console.log(error.message);
          }
        });
  };

  const handleDownloadSummaryOnlineMedia = () => {
    handleCloseDownload();
    setOpenPopupAddLogo(false);
    downloadSummaryOnlineMedia({
      variables: {
        ...payloads,
        page: articles.data.page ?? 1,
        limit: articles.data.limit ?? 20,
        logoUrl: logoPath,
        language: 'English',
        footerTitle: '',
        footerAddress: ''
      },
      onCompleted: () => {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          severity: 'success',
          message:
            'It may take some time for the report to be processed.\n The report can be downloaded from the Activity List on the top right corner.'
        });
        setLogoPath('');
        setLogoName('Choose an image');
      },
      onError(error) {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          severity: 'error',
          message: 'Failed to download report, please try again.'
        });
        setLogoPath('');
        setLogoName('Choose an image');
        console.log(error.message);
      }
    });
  };

  const handleDownloadWhatsappReport = () => {
    const doc = generateWhatsappReportDoc(articles.data.result);
    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, `ONM - ${clippingName}.docx`);
    });
    handleCloseDownload();
  };

  const handleDownloadBySentiment = () => {
    const [selectedLabelId, ...rest] = advanceSearchOnlineMedia.labelList;
    const labelName = labels.find(
      (label) => label.id === selectedLabelId
    )?.displayName;
    const doc = generateArticleBySentimentDoc(
      articles.data.result,
      labelName ?? clippingName
    );
    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, `ONM - ${labelName ?? clippingName}.docx`);
    });
    handleCloseDownload();
  };

  const openDownload = Boolean(anchorElDownload);
  const idPopup = openDownload ? 'simple-popover' : undefined;

  const [includeExcludeOpen, setIncludeExcludeOpen] = useState(false);
  const [includeExcludeMode, setIncludeExcludeMode] = useState<
    'include' | 'exclude'
  >('include');
  const [openSelectGroup, setOpenSelectGroup] = useState(false);
  const {
    groupPortalList,
    selectedGroupPortalIdOnm,
    selectedGroupPortalIdOnmExclude
  } = useAppSelector((state) => state.groupPortal);
  const { filteredList, handleFilter } = useFiltersByName<IGroupPortal>(
    groupPortalList.data
  );

  const { getGroupPortal } = useGroupPortal();

  const handleUpload = async (e) => {
    const inputFiles = e.target.files[0];
    const formData = new FormData();
    formData.append('file', inputFiles);
    const requestOptions = {
      method: 'POST',
      body: formData
    };

    try {
      await fetch(
        'https://v5.external.dashboard.nolimit.id/upload-file/upload-image',
        requestOptions
      )
        .then((response) => response.json())
        .then((data) => {
          if (data?.error) {
            handleClickAlert({
              horizontal: 'center',
              vertical: 'top',
              severity: 'error',
              message:
                data?.error ?? 'Failed to upload image. Please try again.'
            });
            return;
          }
          setLogoPath(data?.path);
          setLogoName(data?.name);
        });
    } catch (error) {
      console.log(error);
      handleClickAlert({
        horizontal: 'center',
        vertical: 'top',
        severity: 'error',
        message: error.error ?? 'Failed to upload image. Please try again.'
      });
    }
  };

  useEffect(() => {
    getGroupPortal(projectId);
  }, [projectId]);
  return (
    <HeaderWrapper display="flex" alignItems="center">
      <Grid container>
        <Grid item sm={12}>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" alignItems="center">
              <Fab
                sx={{
                  background: theme.palette.common.white,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  mr: theme.spacing(2)
                }}
                size="small"
                onClick={() => navigate(ONLINE_MEDIA_CLIPPING)}
              >
                <ArrowBackIcon />
              </Fab>
              <Typography variant="h1">{clippingName}</Typography>
            </Stack>
          </Stack>
        </Grid>
        <Grid item sm={12}>
          <Stack
            direction="row"
            justifyContent="space-between"
            mb={2}
            flexWrap="wrap"
            gap={2}
          >
            <Stack direction="row" flexGrow={1}>
              <Button
                aria-label="select merge strategy"
                aria-haspopup="menu"
                variant="contained"
                sx={{
                  borderStartStartRadius: '8px',
                  borderEndEndRadius: 0,
                  borderStartEndRadius: 0,
                  borderEndStartRadius: '8px'
                }}
                onClick={() => {
                  setIncludeExcludeOpen((prev) => !prev);
                }}
              >
                <ArrowDropDownIcon />
              </Button>
              <FormControl sx={{ width: '150px' }}>
                <Select
                  open={includeExcludeOpen}
                  onOpen={() => setIncludeExcludeOpen(true)}
                  onClose={() => setIncludeExcludeOpen(false)}
                  labelId="group-media"
                  id="group-media"
                  autoWidth
                  value={includeExcludeMode}
                  IconComponent={null}
                  sx={{
                    borderRadius: 0
                  }}
                  onChange={(e) => setIncludeExcludeMode(e.target.value as any)}
                >
                  <MenuItem value={'include'}>Include</MenuItem>
                  <MenuItem value={'exclude'}>Exclude</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ maxWidth: '250px' }}>
                <Select
                  name="group"
                  sx={{
                    borderStartStartRadius: 0,
                    borderEndStartRadius: 0
                  }}
                  open={openSelectGroup}
                  onClose={() => {
                    setOpenSelectGroup(false);
                    if (includeExcludeMode === 'exclude') {
                      dispatch(
                        reducerUpdateAdvanceSearchOnmExcludeMediaList(
                          filteredList
                            .filter((el) =>
                              selectedGroupPortalIdOnmExclude.includes(el.id)
                            )
                            .flatMap((el) => el.portals)
                        )
                      );
                    } else {
                      dispatch(
                        reducerUpdateAdvanceSearchOnmMediaList(
                          filteredList
                            .filter((el) =>
                              selectedGroupPortalIdOnm.includes(el.id)
                            )
                            .flatMap((el) => el.portals)
                        )
                      );
                    }
                  }}
                  onOpen={() => setOpenSelectGroup(true)}
                  value={'always-display'}
                >
                  <MenuItem value={'always-display'} sx={{ display: 'none' }}>
                    {filteredList
                      .filter((el) => {
                        if (includeExcludeMode === 'exclude') {
                          return selectedGroupPortalIdOnmExclude.includes(
                            el.id
                          );
                        } else {
                          return selectedGroupPortalIdOnm.includes(el.id);
                        }
                      })
                      .map((el) => el.displayName)
                      .join(',') || 'Group'}
                  </MenuItem>
                  <Box sx={{ p: theme.spacing(2) }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h4">Group</Typography>
                    </Stack>
                    <Box sx={{ mt: theme.spacing(1) }}>
                      <TableSelectGroupPortal
                        groupPortals={filteredList}
                        selectedGroupByIds={
                          includeExcludeMode === 'exclude'
                            ? selectedGroupPortalIdOnmExclude
                            : selectedGroupPortalIdOnm
                        }
                        loading={groupPortalList.loading}
                        onClick={(group) => {
                          if (includeExcludeMode === 'exclude') {
                            dispatch(
                              toggleSelectedGroupPortalIdOnmExclude(group.id)
                            );
                          } else {
                            dispatch(toggleSelectedGroupPortalIdOnm(group.id));
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Select>
              </FormControl>
            </Stack>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              sx={{
                minWidth: theme.spacing(6),
                height: 36,
                backgroundColor: '#FBFBFB',
                borderRadius: theme.spacing(1)
              }}
              onClick={handleClickDownload}
            >
              Download
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <Popover
        id={idPopup}
        open={openDownload}
        anchorEl={anchorElDownload}
        onClose={handleCloseDownload}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <List>
          <ListItemButton onClick={() => handleDownload()}>
            <ListItemText primary="CSV - All Metrics" />
          </ListItemButton>
          <ListItemButton onClick={() => handleDownloadWhatsappReport()}>
            <ListItemText primary="Word - Whatsapp Report" />
          </ListItemButton>
          <ListItemButton onClick={() => handleDownloadBySentiment()}>
            <ListItemText primary="Word - Articles by Label" />
          </ListItemButton>
          {timezone === 8 && (
            <ListItemButton onClick={() => setOpenPopupAddLogo(true)}>
              <ListItemText primary="PPT - Download Report" />
            </ListItemButton>
          )}
        </List>
      </Popover>
      <Dialog
        onClose={() => {
          setOpenPopupAddLogo(false);
          setLogoPath('');
          setLogoName('Choose an image');
        }}
        open={openPopupAddLogo}
        maxWidth="md"
      >
        <IconButton
          aria-label="close"
          onClick={() => {
            setOpenPopupAddLogo(false);
            setLogoPath('');
            setLogoName('Choose an image');
          }}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ p: theme.spacing(5, 4, 4, 4) }}>
          <Typography variant="h3">
            Download PPT Report from Clipping
          </Typography>
          <Box sx={{ my: theme.spacing(2) }} />
          <Typography variant="subtitle1">
            The report includes a list of articles from clipping with a summary.
            Add your logo to customize the report.
          </Typography>
        </Box>
        <Box sx={{ p: theme.spacing(0, 4, 4, 4) }}>
          <FormControl fullWidth size="medium">
            <FormLabel>Upload your logo</FormLabel>
            <label
              htmlFor="btn-upload"
              style={{
                cursor: 'pointer',
                marginBottom: theme.spacing(0.5)
              }}
            >
              <input
                id="btn-upload"
                name="btn-upload"
                style={{ display: 'none' }}
                type="file"
                accept="image/*"
                onChange={handleUpload}
              />
              <ContainerUpload>
                <Typography variant="caption">{logoName}</Typography>
                <Button
                  variant="contained"
                  component="span"
                  color="primary"
                  sx={{ height: '34px', borderRadius: theme.spacing(1) }}
                >
                  Choose Image
                </Button>
              </ContainerUpload>
            </label>
            <Typography variant="caption">
              Image size: no more than 300kB in jpeg or png.{' '}
            </Typography>
          </FormControl>
        </Box>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            disabled={resDownloadSummaryOnlineMedia.loading}
            size="large"
            sx={{ px: theme.spacing(4) }}
            onClick={handleDownloadSummaryOnlineMedia}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </HeaderWrapper>
  );
}

export default HeaderOnlineMediaClippingById;
